import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { Search, Loader2, Disc3 } from "lucide-react";
import { toast } from "sonner";

export default function DiscogsSearchDialog({ open, onOpenChange, onPick }) {
    const [mode, setMode] = useState("text");
    const [query, setQuery] = useState({ artist: "", title: "", barcode: "", catno: "" });
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [fetchingId, setFetchingId] = useState(null);

    const set = (k, v) => setQuery(q => ({ ...q, [k]: v }));

    const reset = () => {
        setQuery({ artist: "", title: "", barcode: "", catno: "" });
        setResults([]);
    };

    const handleSearch = async (e) => {
        e?.preventDefault?.();
        const hasInput = Object.values(query).some(v => v.trim());
        if (!hasInput) {
            toast.error("Enter at least one search term");
            return;
        }
        setSearching(true);
        try {
            const { data } = await api.get("/discogs/search", { params: query });
            setResults(data.results || []);
            if ((data.results || []).length === 0) {
                toast.info("No matches found on Discogs");
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Discogs search failed");
        } finally {
            setSearching(false);
        }
    };

    const handlePick = async (releaseId) => {
        setFetchingId(releaseId);
        try {
            const { data } = await api.get(`/discogs/releases/${releaseId}`);
            onPick(data);
            reset();
            onOpenChange(false);
            toast.success("Imported from Discogs");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to fetch release");
        } finally {
            setFetchingId(null);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-card" data-testid="discogs-search-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display text-3xl font-bold tracking-tight flex items-center gap-2">
                        <Disc3 className="w-6 h-6 text-primary" strokeWidth={1.5} />
                        Search Discogs
                    </DialogTitle>
                    <DialogDescription>
                        Find a release and we&apos;ll auto-fill artist, album, year, label and artwork.
                    </DialogDescription>
                </DialogHeader>

                <div className="flex gap-2 pb-2">
                    <Button
                        type="button"
                        variant={mode === "text" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setMode("text")}
                        data-testid="discogs-mode-text"
                    >
                        Artist + Album
                    </Button>
                    <Button
                        type="button"
                        variant={mode === "code" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setMode("code")}
                        data-testid="discogs-mode-code"
                    >
                        Barcode / Catalog #
                    </Button>
                </div>

                <form onSubmit={handleSearch} className="space-y-3">
                    {mode === "text" ? (
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="overline">Artist</Label>
                                <Input
                                    value={query.artist}
                                    onChange={(e) => set("artist", e.target.value)}
                                    placeholder="e.g. Pink Floyd"
                                    className="mt-1.5"
                                    data-testid="discogs-input-artist"
                                />
                            </div>
                            <div>
                                <Label className="overline">Album title</Label>
                                <Input
                                    value={query.title}
                                    onChange={(e) => set("title", e.target.value)}
                                    placeholder="e.g. Dark Side of the Moon"
                                    className="mt-1.5"
                                    data-testid="discogs-input-title"
                                />
                            </div>
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="overline">Barcode</Label>
                                <Input
                                    value={query.barcode}
                                    onChange={(e) => set("barcode", e.target.value)}
                                    placeholder="EAN / UPC"
                                    className="mt-1.5"
                                    data-testid="discogs-input-barcode"
                                />
                            </div>
                            <div>
                                <Label className="overline">Catalog #</Label>
                                <Input
                                    value={query.catno}
                                    onChange={(e) => set("catno", e.target.value)}
                                    placeholder="e.g. SHVL 804"
                                    className="mt-1.5"
                                    data-testid="discogs-input-catno"
                                />
                            </div>
                        </div>
                    )}
                    <Button
                        type="submit"
                        disabled={searching}
                        className="bg-primary hover:bg-primary/90 text-primary-foreground gap-2"
                        data-testid="discogs-search-btn"
                    >
                        {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                        Search
                    </Button>
                </form>

                {results.length > 0 && (
                    <div className="mt-4 space-y-2 max-h-[400px] overflow-y-auto pr-1" data-testid="discogs-results">
                        {results.map(r => (
                            <button
                                key={r.discogs_id}
                                onClick={() => handlePick(r.discogs_id)}
                                disabled={fetchingId !== null}
                                className="w-full flex items-center gap-4 p-3 rounded-md border border-border hover:border-primary hover:bg-primary/5 transition-colors text-left disabled:opacity-50"
                                data-testid={`discogs-result-${r.discogs_id}`}
                            >
                                <div className="w-14 h-14 rounded bg-secondary overflow-hidden shrink-0">
                                    {r.thumb ? (
                                        <img src={r.thumb} alt="" className="w-full h-full object-cover" />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center">
                                            <Disc3 className="w-6 h-6 text-muted-foreground" />
                                        </div>
                                    )}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="font-medium text-sm truncate">{r.title}</p>
                                    <p className="text-xs text-muted-foreground truncate">
                                        {[r.year, r.label, r.country, r.catno && `cat# ${r.catno}`]
                                            .filter(Boolean).join(" · ")}
                                    </p>
                                    <p className="text-[11px] text-muted-foreground/80 truncate mt-0.5">{r.format}</p>
                                </div>
                                {fetchingId === r.discogs_id && (
                                    <Loader2 className="w-4 h-4 animate-spin text-primary" />
                                )}
                            </button>
                        ))}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
